import React, { useState } from "react";
import { motion } from "framer-motion";
import { PUBLIC_CALENDARS_BY_YEAR, YEARS, dateKey, DEFAULT_OCCUPANCY, EXAMPLE_OCCUPANCY_BY_KEY } from "../data";
import { Calendar as CalendarIcon, Check, Lock, Sparkles, Users } from "lucide-react";
import BedAvailability from "./BedAvailability";

// Alterne para true quando quiser voltar a exibir a quantidade de vagas
const SHOW_VAGAS = true;

const Calendar = ({ onPickDate, availabilityMap }) => {
  const [modalDate, setModalDate] = useState(null);
  const [year, setYear] = useState(2026);

  const getOccupancyFor = (month, range) => {
    const key = dateKey(month, range, year);
    if (availabilityMap && availabilityMap[key]) {
      const entry = availabilityMap[key];
      // New format: {occupancy, restrictions}
      if (entry.occupancy) return { occupancy: entry.occupancy, restrictions: entry.restrictions || {} };
      // Legacy fallback: raw occupancy object
      return { occupancy: entry, restrictions: {} };
    }
    if (EXAMPLE_OCCUPANCY_BY_KEY[key]) return { occupancy: EXAMPLE_OCCUPANCY_BY_KEY[key], restrictions: {} };
    return {
      occupancy: {
        cabineCasal: [false, false],
        suiteTripla: [false, false, false],
        suiteCasal: [false, false],
        cabineQuadrupla: [false, false, false, false],
      },
      restrictions: {},
    };
  };

  const openDate = (m, d) => {
    if (d.status !== "available") return;
    const { occupancy, restrictions } = getOccupancyFor(m.month, d.range);
    setModalDate({
      month: m.month,
      range: d.range,
      days: d.days,
      dateStr: `${d.range} de ${m.month}/${year}`,
      occupancy,
      restrictions,
    });
  };

  const handleReserveFromModal = (cabin) => {
    if (modalDate) {
      onPickDate(modalDate.dateStr, modalDate.days, cabin || "", dateKey(modalDate.month, modalDate.range, year));
      setModalDate(null);
    }
  };

  return (
    <section id="agenda" className="relative py-24 md:py-32" style={{ background: "var(--ocean-deep)" }} data-testid="calendar-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-10 items-end mb-12">
          <div>
            <div className="overline mb-4 flex items-center gap-2">
              <CalendarIcon size={14} /> Agenda {year}
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight">
              Escolha sua data
              <span className="block" style={{ color: "var(--ocean-accent)" }}>de embarque.</span>
            </h2>
          </div>
          <p className="text-base" style={{ color: "var(--ocean-muted)" }}>
            As vagas são limitadas a 11 hóspedes por saída. Clique em uma data disponível para ver
            quantas cabines já foram reservadas e onde estão as vagas livres no catamarã.
          </p>
        </div>

        {/* Year tabs */}
        <p
          className="text-lg md:text-xl mb-3"
          style={{ color: "var(--ocean-muted)" }}
          data-testid="calendar-year-hint"
        >
          Selecione o ano para verificar as datas disponíveis
        </p>
        <div className="flex gap-2 mb-6" data-testid="calendar-year-tabs">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className="px-5 py-2 text-sm transition"
              style={{
                background: year === y ? "var(--ocean-accent)" : "transparent",
                color: year === y ? "#04111E" : "var(--ocean-muted)",
                border: `1px solid ${year === y ? "var(--ocean-accent)" : "var(--ocean-border)"}`,
              }}
              data-testid={`calendar-year-tab-${y}`}
            >
              {y}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-6 mb-8 text-xs" data-testid="calendar-legend">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3" style={{ background: "var(--ocean-accent)" }} />
            <span>Disponível · clique para ver vagas</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 opacity-30" style={{ background: "var(--ocean-muted)" }} />
            <span style={{ color: "var(--ocean-muted)" }}>Reservado</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles size={12} style={{ color: "var(--ocean-coral)" }} />
            <span style={{ color: "var(--ocean-coral)" }}>Edição especial</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PUBLIC_CALENDARS_BY_YEAR[year].map((m, mIdx) => (
            <motion.div
              key={`${year}-${m.month}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: mIdx * 0.05 }}
              className="glass p-5"
            >
              <div className="font-display text-2xl mb-4 pb-3 border-b" style={{ borderColor: "var(--ocean-border)" }}>
                {m.month}
              </div>
              <ul className="space-y-1.5">
                {m.dates.map((d) => {
                  const isAvail = d.status === "available";
                  const occ = isAvail ? getOccupancyFor(m.month, d.range).occupancy : null;
                  const taken = occ
                    ? Object.values(occ).reduce((s, arr) => s + arr.filter(Boolean).length, 0)
                    : 0;
                  const free = 11 - taken;
                  return (
                    <li key={d.range}>
                      <button
                        onClick={() => (isAvail ? openDate(m, d) : null)}
                        disabled={!isAvail}
                        className={`w-full flex items-center justify-between text-left px-3 py-2 text-xs transition ${
                          isAvail ? "hover:bg-white/5 cursor-pointer" : "cursor-not-allowed opacity-40"
                        }`}
                        style={{
                          background: isAvail ? "rgba(245,199,84,0.06)" : "rgba(255,255,255,0.02)",
                          border: `1px solid ${isAvail ? "rgba(245,199,84,0.30)" : "rgba(255,255,255,0.05)"}`,
                        }}
                        data-testid={`calendar-date-${year}-${m.month}-${d.range.replace(/[^\w]/g, "")}`}
                      >
                        <span className="flex items-center gap-2">
                          {isAvail ? (
                            <Check size={11} style={{ color: "var(--ocean-accent)" }} />
                          ) : (
                            <Lock size={10} style={{ color: "var(--ocean-muted)" }} />
                          )}
                          <span>{d.range}</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          {d.special && (
                            <span
                              className="text-[0.6rem] px-1.5 py-0.5 uppercase tracking-wider"
                              style={{ background: "var(--ocean-coral)", color: "#fff" }}
                            >
                              {d.special}
                            </span>
                          )}
                          {isAvail && SHOW_VAGAS && (
                            <span
                              className="text-[0.6rem] px-1.5 py-0.5 flex items-center gap-1 font-semibold"
                              style={{
                                background: free === 0 ? "rgba(224,85,72,0.15)" : "rgba(31,168,102,0.15)",
                                color: free === 0 ? "#FF8B7F" : "var(--ocean-accent)",
                              }}
                            >
                              <Users size={9} /> {free}
                            </span>
                          )}
                          {!isAvail && !d.special && (
                            <span className="text-[0.55rem] tracking-wider uppercase" style={{ color: "var(--ocean-muted)" }}>
                              Reservado
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="mt-12 grid sm:grid-cols-3 gap-3" data-testid="calendar-actions">
          <button
            onClick={() => onPickDate("", null)}
            className="glass lift p-6 text-left"
            data-testid="calendar-pick-later"
          >
            <div className="overline mb-2">Opção 01</div>
            <div className="font-display text-xl mb-1">Escolher data depois</div>
            <div className="text-xs" style={{ color: "var(--ocean-muted)" }}>
              Reservar agora e definir a data com nossa equipe.
            </div>
          </button>
          <button
            onClick={() => onPickDate("DATA PERSONALIZADA — favor consultar disponibilidade", null)}
            className="glass lift p-6 text-left"
            data-testid="calendar-custom-date"
          >
            <div className="overline mb-2">Opção 02</div>
            <div className="font-display text-xl mb-1">Solicitar data diferente</div>
            <div className="text-xs" style={{ color: "var(--ocean-muted)" }}>
              Grupos fechados ou datas específicas sob consulta.
            </div>
          </button>
          <a
            href="#reserva"
            className="lift p-6 text-left"
            style={{ background: "var(--ocean-accent)", color: "#04111E" }}
            data-testid="calendar-go-to-form"
          >
            <div className="overline mb-2" style={{ color: "#04111E" }}>Opção 03</div>
            <div className="font-display text-xl mb-1">Ir para o formulário</div>
            <div className="text-xs opacity-80">
              Preencha seus dados e finalize via WhatsApp.
            </div>
          </a>
        </div>
      </div>

      <BedAvailability
        open={!!modalDate}
        onClose={() => setModalDate(null)}
        date={modalDate ? `${modalDate.range} de ${modalDate.month}/${year}` : ""}
        month={modalDate?.month}
        days={modalDate?.days}
        occupancy={modalDate?.occupancy}
        restrictions={modalDate?.restrictions}
        onReserve={handleReserveFromModal}
      />
    </section>
  );
};

export default Calendar;